import { useMutation } from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import { Icon } from '../../components/Icon.js'
import { Modal, ModalCloseButton } from '../../components/Modal.js'
import { Badge, Button, Input } from '../../components/ui.js'
import { useBandDrag } from '../../lib/band-drag.js'
import { cn } from '../../lib/cn.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'

type MaterialType = RouterOutputs['catalog']['materialTypes'][number]

const BAND = 'types'

/**
 * The office's list of item types.
 *
 * The order is the order the Type sort reads in, so it is draggable, on the
 * same hand-written gesture as the kinds-of-work list. Removing follows rule
 * 23: retire takes a type off the pickers and leaves it on the items that
 * have it, and delete is only for one nothing carries.
 */
export function MaterialTypesDialog({
  open,
  types,
  onClose,
  onChanged,
}: {
  open: boolean
  types: MaterialType[]
  onClose: () => void
  onChanged: () => void
}) {
  const trpc = useTRPC()
  const [name, setName] = useState('')

  const upsert = useMutation(
    trpc.catalog.upsertMaterialType.mutationOptions({ onSuccess: onChanged }),
  )
  const archive = useMutation(
    trpc.catalog.archiveMaterialType.mutationOptions({ onSuccess: onChanged }),
  )
  const remove = useMutation(
    trpc.catalog.deleteMaterialType.mutationOptions({ onSuccess: onChanged }),
  )
  const reorder = useMutation(
    trpc.catalog.reorderMaterialTypes.mutationOptions({ onSettled: onChanged }),
  )

  const drag = useBandDrag({
    enabled: true,
    onDrop: (id, _band, before) => {
      if (before === id) return

      const ids = types.map((type) => type.id)
      const without = ids.filter((other) => other !== id)
      const at = before && without.includes(before) ? without.indexOf(before) : without.length
      const orderedIds = [...without.slice(0, at), id, ...without.slice(at)]

      if (orderedIds.every((value, index) => value === ids[index])) return
      reorder.mutate({ orderedIds })
    },
  })

  const add = () => {
    const trimmed = name.trim()
    if (!trimmed) return
    upsert.mutate({ name: trimmed }, { onSuccess: () => setName('') })
  }

  const error = upsert.error ?? archive.error ?? remove.error ?? reorder.error
  const carried = drag.carrying ? types.find((type) => type.id === drag.carrying?.id) : undefined

  const caret = (beforeId: string | null) =>
    drag.carrying && drag.before === beforeId && drag.carrying.id !== beforeId ? (
      <li aria-hidden className="relative h-0">
        <span className="absolute inset-x-2 -top-px h-0.5 rounded-full bg-brand-600" />
      </li>
    ) : null

  return (
    <Modal open={open} onClose={onClose} labelledBy="material-types-title" className="max-w-lg">
      <div className="flex items-center justify-between gap-2 border-b border-ink-200 px-4 py-3">
        <h2 id="material-types-title" className="text-sm font-semibold text-ink-900">
          Item types
        </h2>
        <ModalCloseButton onClose={onClose} />
      </div>

      <p className="px-4 pt-3 text-xs text-ink-500">
        Your own grouping for catalog items. Drag to set the order the Type sort uses.
      </p>

      {error ? (
        <p role="alert" className="mx-4 mt-2 rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-700">
          {errorMessage(error)}
        </p>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {types.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs text-ink-500">No types yet. Add the first below.</p>
        ) : (
          <ul data-band={BAND} className="mt-2 divide-y divide-ink-100 border-y border-ink-100">
            {types.map((type) => {
              const retired = type.archivedAt !== null
              const count = type._count.materials

              return (
                <Fragment key={type.id}>
                  {caret(type.id)}
                  <li
                    data-row={type.id}
                    onPointerDown={(event) => drag.begin(event, type.id, BAND)}
                    className={cn(
                      'flex cursor-grab items-center gap-2 px-4 py-1.5',
                      retired && 'bg-ink-50',
                      drag.carrying?.id === type.id && 'opacity-40',
                    )}
                  >
                    <span aria-hidden className="w-4 shrink-0 text-ink-300">
                      <Icon name="grip" className="size-4" />
                    </span>

                    <Input
                      key={type.name}
                      defaultValue={type.name}
                      aria-label={`Name of ${type.name}`}
                      className="min-w-24 flex-1 border-0 bg-transparent px-1.5 shadow-none ring-0 hover:bg-white hover:ring-1 hover:ring-ink-200 focus:bg-white focus:ring-2"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') event.currentTarget.blur()
                        if (event.key === 'Escape') {
                          event.currentTarget.value = type.name
                          event.currentTarget.blur()
                        }
                      }}
                      onBlur={(event) => {
                        const next = event.target.value.trim()
                        if (next && next !== type.name) upsert.mutate({ id: type.id, name: next })
                      }}
                    />

                    {retired ? <Badge tone="amber">Retired</Badge> : null}

                    <span className="w-16 shrink-0 text-right text-xs tabular-nums text-ink-500">
                      {count.toLocaleString('en-AU')} {count === 1 ? 'item' : 'items'}
                    </span>

                    <Button
                      size="sm"
                      variant="ghost"
                      data-no-drag
                      aria-label={retired ? `Bring back ${type.name}` : `Retire ${type.name}`}
                      onClick={() => archive.mutate({ id: type.id, archived: !retired })}
                    >
                      {retired ? 'Bring back' : 'Retire'}
                    </Button>

                    <Button
                      size="sm"
                      variant="ghost"
                      data-no-drag
                      disabled={count > 0}
                      aria-label={`Delete ${type.name}`}
                      title={
                        count > 0
                          ? 'Items carry this type, so it can only be retired.'
                          : `Delete ${type.name}`
                      }
                      onClick={() => remove.mutate({ id: type.id })}
                    >
                      <Icon name="trash" className="size-3.5" />
                    </Button>
                  </li>
                </Fragment>
              )
            })}
            {caret(null)}
          </ul>
        )}
      </div>

      <form
        className="flex items-center gap-2 border-t border-ink-200 px-4 py-3"
        onSubmit={(event) => {
          event.preventDefault()
          add()
        }}
      >
        <Input
          value={name}
          placeholder="Chemical, Bait station, Callout fee…"
          aria-label="New type"
          className="min-w-0 flex-1"
          onChange={(event) => setName(event.target.value)}
        />
        <Button type="submit" variant="secondary" loading={upsert.isPending}>
          Add
        </Button>
      </form>

      {carried ? (
        <div
          ref={drag.ghostRef}
          aria-hidden
          className="pointer-events-none fixed top-0 left-0 z-[100] rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-xs font-medium text-ink-900 shadow-lg"
        >
          {carried.name}
        </div>
      ) : null}
    </Modal>
  )
}
